import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, GitCommit } from "lucide-react";
import api from "@/lib/api";
import { formatDateTime, relTime } from "@/lib/format";

const OP_LABEL = {
  insert: "Created",
  update: "Updated",
  soft_delete: "Deleted",
  restore: "Restored",
  purge: "Purged",
};

const OP_TONE = {
  insert: "text-emerald-600 dark:text-emerald-400",
  update: "text-blue-600 dark:text-blue-400",
  soft_delete: "text-red-600 dark:text-red-400",
  restore: "text-amber-600 dark:text-amber-400",
};

/**
 * HistoryDialog — regulatory diff viewer.
 * Loads GET /history/{entity_type}/{entity_id}, shows a chronological list
 * with before/after JSON diffs. Manager+ only (backend enforces 403).
 */
export default function HistoryDialog({ open, onOpenChange, entityType, entityId, entityLabel }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!open || !entityType || !entityId) return;
    setRows(null);
    api.get(`/history/${entityType}/${entityId}`)
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]));
  }, [open, entityType, entityId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitCommit size={16} /> Change history
          </DialogTitle>
          <DialogDescription>
            {entityLabel ? <>Regulated diff log for <span className="font-medium">{entityLabel}</span>.</> : "Regulated diff log."}
          </DialogDescription>
        </DialogHeader>

        {rows === null && (
          <div className="py-8 text-sm text-muted-foreground text-center">
            <Loader2 size={14} className="animate-spin inline mr-2" /> Loading history…
          </div>
        )}

        {rows?.length === 0 && (
          <div className="py-6 text-sm text-muted-foreground text-center">
            No history recorded yet.
          </div>
        )}

        {rows && rows.length > 0 && (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.id} className="border border-border rounded-md p-3" data-testid={`history-row-${r.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs font-medium uppercase tracking-widest ${OP_TONE[r.operation] || "text-muted-foreground"}`}>
                    {OP_LABEL[r.operation] || r.operation}
                  </span>
                  <span className="text-xs text-muted-foreground">by</span>
                  <span className="text-xs font-medium">{r.actor_name || "system"}</span>
                  <span className="text-xs text-muted-foreground ml-auto font-mono" title={formatDateTime(r.created_at)}>
                    {relTime(r.created_at)}
                  </span>
                </div>
                {r.change_reason && (
                  <div className="text-xs italic text-muted-foreground mb-2">
                    Reason: “{r.change_reason}”
                  </div>
                )}
                {(r.before || r.after) && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <DiffBox title="Before" data={r.before} />
                    <DiffBox title="After" data={r.after} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DiffBox({ title, data }) {
  if (!data) return (
    <div className="text-xs">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{title}</div>
      <div className="text-muted-foreground italic">—</div>
    </div>
  );
  return (
    <div className="text-xs">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{title}</div>
      <pre className="font-mono text-[11px] bg-muted/50 p-2 rounded-md whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}
